import { useEffect, useState } from 'react'

const formatNumber = value => {
  if (value == null || value === '') return value

  const number = Number(value)
  if (Number.isNaN(number)) return value

  return number.toLocaleString('es-CL')
}

/*const formatCurrency = amount => {
  if (amount == null || Number.isNaN(Number(amount))) return null

  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0
  }).format(Number(amount))
}*/

const PlanStatusBadge = ({ isSelected, isSelectedForUpdate, isPlanCancelled }) => {
  if (isSelected && isPlanCancelled) {
    return (
      <span className='inline-flex items-center rounded-full bg-gray-500 px-3 py-1 text-[11px] font-sans font-semibold uppercase tracking-[0.16em] text-white'>
        Plan cancelado
      </span>
    )
  }

  if (isSelected) {
    return (
      <span className='inline-flex items-center rounded-full bg-green-500 px-3 py-1 text-[11px] font-sans font-semibold uppercase tracking-[0.16em] text-white'>
        Plan actual
      </span>
    )
  }

  if (isSelectedForUpdate) {
    return (
      <span className='inline-flex items-center rounded-full bg-accent2 px-3 py-1 text-[11px] font-sans font-semibold uppercase tracking-[0.16em] text-white'>
        Seleccionado
      </span>
    )
  }

  return (
    <span className='inline-flex items-center rounded-full border border-gray-200 bg-white px-3 py-1 text-[11px] font-sans font-semibold uppercase tracking-[0.16em] text-gray-500'>
      Disponible
    </span>
  )
}

const FeatureItem = ({ children }) => (
  <li className='flex items-start gap-2 text-sm leading-6 text-gray-600'>
    <svg
      className='mt-1 h-4 w-4 shrink-0 text-green-500'
      viewBox='0 0 20 20'
      fill='currentColor'
      aria-hidden='true'
    >
      <path
        fillRule='evenodd'
        d='M16.704 5.29a1 1 0 010 1.42l-7.072 7.07a1 1 0 01-1.414 0L3.29 8.854a1 1 0 011.414-1.414l4.221 4.22 6.365-6.37a1 1 0 011.414 0z'
        clipRule='evenodd'
      />
    </svg>
    <span>{children}</span>
  </li>
)

const PlanItem = ({
  plan,
  isSelected,
  isSelectedForUpdate,
  onSelect,
  ufValue = 0,
  onLayoutChange,
  onChangePlanClick,
  onCancelPlanClick,
  onResumePlanClick
}) => {
  const [showIncludedFeatures, setShowIncludedFeatures] = useState(false)

  useEffect(() => {
    if (typeof onLayoutChange !== 'function') return

    const frameId = requestAnimationFrame(() => {
      onLayoutChange()
    })

    return () => cancelAnimationFrame(frameId)
  }, [showIncludedFeatures, onLayoutChange])

  const isPlanCancelled = plan?.isCancelled === true

  const frequencyLabels = {
    monthly: 'Mensual',
    yearly: 'Anual'
  }

  const frequencyLabel = frequencyLabels[plan.frequency] || plan.frequency
  const billingLabel = plan.frequency === 'yearly' ? 'al año' : 'al mes'
  const hasSelectableAction = typeof onSelect === 'function'
  const hasPrice = plan.ufPriceWithoutTaxes !== null && plan.ufPriceWithoutTaxes !== undefined
  const basePrice = hasPrice ? Number(plan.ufPriceWithoutTaxes) : null
  const hasDiscount = hasPrice && Boolean(plan.upgradeDiscountUf && plan.upgradeDiscountUf > 0)
  const discountedPrice = hasDiscount ? basePrice - Number(plan.upgradeDiscountUf) : null
  //const priceInClp = hasPrice && ufValue ? Math.round(basePrice * Number(ufValue) * 1.19) : null
  //const discountedPriceInClp = discountedPrice != null && ufValue ? Math.round(discountedPrice * Number(ufValue) * 1.19) : null

  const handleCardClick = () => {
    if (hasSelectableAction) {
      onSelect(plan.id)
    }
  }

  const handleSelectClick = event => {
    event.stopPropagation()

    if (hasSelectableAction) {
      onSelect(plan.id)
    }
  }

  const cardTone = isSelected
    ? isPlanCancelled
      ? {
          shell: 'border-gray-400 bg-gradient-to-b from-gray-50 via-white to-white shadow-lg',
          topBar: 'bg-gradient-to-r from-gray-500 via-gray-400 to-gray-500',
          pricePanel: 'border-gray-100 bg-white/95',
          action: 'border-gray-500 bg-grey-500 text-white'
        }
      : {
          shell: 'border-green-500 bg-gradient-to-b from-green-50 via-white to-white shadow-lg',
          topBar: 'bg-gradient-to-r from-green-500 via-green-400 to-green-500',
          pricePanel: 'border-green-100 bg-white/95',
          action: 'border-green-500 bg-green-500 text-white'
        }
    : isSelectedForUpdate
      ? {
          shell: 'border-accent2 bg-gradient-to-b from-accent2/10 via-white to-white shadow-lg',
          topBar: 'bg-gradient-to-r from-accent2 via-primary to-accent2',
          pricePanel: 'border-accent2/15 bg-white/95',
          action: 'border-accent2 bg-accent2 text-white'
        }
      : {
          shell:
            'border-gray-200 bg-gradient-to-b from-white via-white to-gray-50/70 shadow-sm hover:-translate-y-1 hover:border-accent2/30 hover:shadow-xl',
          topBar: 'bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200',
          pricePanel: 'border-gray-200 bg-white/90',
          action:
            'border-gray-200 bg-white text-gray-700 hover:border-accent2/35 hover:text-accent2'
        }

  return (
    <div
      className={`group relative flex h-full flex-col overflow-hidden rounded-[28px] border transition-all duration-200 ${
        cardTone.shell
      } ${hasSelectableAction ? 'cursor-pointer' : ''}`}
      onClick={handleCardClick}
    >
      <div className={`absolute inset-x-0 top-0 h-1.5 ${cardTone.topBar}`} />

      <div className='flex h-full flex-col p-6 sm:p-7'>
        <div className='flex items-start justify-between gap-3'>
          <div className='flex flex-wrap items-center gap-2'>
            <span className='inline-flex items-center rounded-full border border-gray-200 bg-white px-3 py-1 text-[11px] font-sans font-semibold uppercase tracking-[0.16em] text-gray-500'>
              {frequencyLabel}
            </span>
            {plan.freeTrialDays != null && plan.freeTrialDays > 0 && (
              <span className='inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-[11px] font-sans font-semibold uppercase tracking-[0.16em] text-green-700'>
                {formatNumber(plan.freeTrialDays)} dias gratis
              </span>
            )}
          </div>
          <PlanStatusBadge
            isSelected={isSelected}
            isSelectedForUpdate={isSelectedForUpdate}
            isPlanCancelled={isPlanCancelled}
          />
        </div>

        <div className='mt-2'>
          <h3 className='text-2xl font-sans font-semibold tracking-tight text-gray-900 sm:text-[1.9rem]'>
            {plan.name}
          </h3>
          <p className='mt-2 min-h-[48px] text-sm font-sans leading-6 text-gray-600'>
            {plan.description ||
              'Plan diseñado para escalar contigo manteniendo el mismo flujo de trabajo.'}
          </p>
        </div>

        {isSelected && isPlanCancelled && (
          <div className='mt-4 rounded-2xl border border-gray-200 bg-gray-50 p-4'>
            <p className='text-xs font-sans font-semibold uppercase tracking-[0.16em] text-gray-700'>
              Estado del plan
            </p>
            <p className='mt-2 text-sm font-sans leading-6 text-gray-900'>
              Este plan está cancelado y seguirá vigente hasta el fin del período actual. Puedes
              retomarlo o cambiarte a otro plan.
            </p>
          </div>
        )}

        <div className={`mt-2 rounded-[24px] border p-5 ${cardTone.pricePanel}`}>
          <div className='flex items-start justify-between gap-4'>
            <div>
              <p className='text-[11px] font-sans font-semibold uppercase tracking-[0.18em] text-gray-500'>
                Precio
              </p>
              {hasPrice ? (
                <>
                  <div className='mt-2 flex flex-wrap items-end gap-2'>
                    <span className='text-4xl font-bold leading-none text-gray-900 sm:text-[2.6rem]'>
                      {formatNumber(basePrice)} UF
                    </span>
                    <span className='pb-1 text-sm font-sans font-medium text-gray-500'>
                      + IVA {billingLabel}
                    </span>
                  </div>
                </>
              ) : (
                <p className='mt-2 text-lg font-sans font-semibold text-gray-800'>Precio a convenir</p>
              )}
            </div>

            {hasSelectableAction && (
              <button
                type='button'
                onClick={handleSelectClick}
                className={`inline-flex shrink-0 items-center rounded-full border px-4 py-2 text-sm font-semibold transition-all ${cardTone.action}`}
              >
                {isSelectedForUpdate ? 'Elegido' : 'Seleccionar'}
              </button>
            )}
          </div>

          {hasDiscount && discountedPrice != null && (
            <div className='mt-4 rounded-2xl border border-accent2/15 bg-accent2/5 p-4'>
              <div className='flex flex-wrap items-center justify-between gap-3'>
                <div>
                  <p className='text-xs font-sans font-semibold uppercase tracking-[0.16em] text-accent2'>
                    Descuento por adelanto
                  </p>
                  <p className='mt-1 text-sm font-sans text-gray-600'>
                    Se descuenta{' '}
                    <span className='font-sans font-semibold text-accent2'>
                      -{formatNumber(plan.upgradeDiscountUf)} UF
                    </span>{' '}
                    en el primer pago.
                  </p>
                </div>
                <div className='text-right'>
                  <p className='text-xs font-sans text-gray-500'>Primer pago</p>
                  <p className='text-lg font-sans font-bold text-gray-900'>
                    {discountedPrice.toFixed(2)} UF + IVA
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {hasSelectableAction && (
          <button
            type='button'
            onClick={handleSelectClick}
            className={`mt-5 flex w-full items-center justify-between rounded-[20px] border px-4 py-3 text-left text-sm font-sans font-semibold transition-all ${cardTone.action}`}
          >
            <span>
              {isSelectedForUpdate ? 'Plan listo para continuar' : 'Seleccionar este plan'}
            </span>
            <span className='text-xs opacity-80'>
              {isSelectedForUpdate ? 'Confirmado' : 'Click para elegir'}
            </span>
          </button>
        )}

        {plan.includedFeatures && plan.includedFeatures.length > 0 && (
          <div className='mt-6 rounded-[24px] border border-gray-200 bg-gray-50/80 p-4'>
            <button
              type='button'
              onClick={event => {
                event.stopPropagation()
                setShowIncludedFeatures(prev => !prev)
              }}
              className='flex w-full items-center justify-between gap-3 text-left'
            >
              <div>
                <h4 className='text-sm font-sans font-semibold text-gray-800'>Incluye</h4>
                <p className='mt-1 text-xs font-sans text-gray-500'>
                  {plan.includedFeatures.length}{' '}
                  {plan.includedFeatures.length === 1
                    ? 'caracteristica incluida'
                    : 'caracteristicas incluidas'}
                </p>
              </div>
              <span className='flex items-center gap-2 text-sm font-sans font-semibold text-accent2'>
                {showIncludedFeatures ? 'Ocultar' : 'Ver detalle'}
                <svg
                  className={`h-4 w-4 transition-transform ${
                    showIncludedFeatures ? 'rotate-180' : ''
                  }`}
                  viewBox='0 0 20 20'
                  fill='currentColor'
                  aria-hidden='true'
                >
                  <path
                    fillRule='evenodd'
                    d='M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z'
                    clipRule='evenodd'
                  />
                </svg>
              </span>
            </button>

            {showIncludedFeatures && (
              <ul className='mt-4 space-y-2 border-t border-gray-200 pt-4'>
                {plan.includedFeatures.map((feature, index) => (
                  <FeatureItem key={index}>{feature}</FeatureItem>
                ))}
              </ul>
            )}
          </div>
        )}

        {isSelected && (onChangePlanClick || onCancelPlanClick || onResumePlanClick) && (
          <div className='mt-6 border-t border-gray-200 pt-5'>
            <div
              className={`grid grid-cols-1 gap-3 ${
                isPlanCancelled
                  ? onChangePlanClick && onResumePlanClick
                    ? 'sm:grid-cols-2'
                    : ''
                  : onChangePlanClick && onCancelPlanClick
                    ? 'sm:grid-cols-2'
                    : ''
              }`}
            >
              {isPlanCancelled ? (
                <>
                  {onResumePlanClick && (
                    <button
                      type='button'
                      onClick={event => {
                        event.stopPropagation()
                        onResumePlanClick()
                      }}
                      className='rounded-full bg-accent2 px-4 py-3 text-sm font-sans font-medium text-white transition-opacity hover:bg-opacity-90'
                    >
                      Retomar plan
                    </button>
                  )}
                  {onChangePlanClick && (
                    <button
                      type='button'
                      onClick={event => {
                        event.stopPropagation()
                        onChangePlanClick()
                      }}
                      className='rounded-full border border-accent2 bg-white px-4 py-3 text-sm font-sans font-medium text-accent2 transition-colors hover:bg-accent2/5'
                    >
                      Cambiar plan
                    </button>
                  )}
                </>
              ) : (
                <>
                  {onCancelPlanClick && (
                    <button
                      type='button'
                      onClick={event => {
                        event.stopPropagation()
                        onCancelPlanClick()
                      }}
                      className='rounded-full bg-accent3 px-4 py-3 text-sm font-sans font-medium text-white transition-opacity hover:bg-opacity-90'
                    >
                      Cancelar plan
                    </button>
                  )}
                  {onChangePlanClick && (
                    <button
                      type='button'
                      onClick={event => {
                        event.stopPropagation()
                        onChangePlanClick()
                      }}
                      className='rounded-full bg-accent2 px-4 py-3 text-sm font-sans font-medium text-white transition-opacity hover:bg-opacity-90'
                    >
                      Cambiar plan
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default PlanItem
